import { Link } from "react-router-dom";
import { Zap, Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { SITE, SERVICE_IDS } from "@/data/site";

export default function Footer() {
  const { t } = useLang();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-navy text-slate-300" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-2.5 mb-4">
            <span className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
              <Zap className="w-5 h-5 text-amber2" fill="currentColor" />
            </span>
            <span className="font-display font-extrabold tracking-tight text-white text-lg">NEXTRON</span>
          </div>
          <p className="text-sm leading-relaxed text-slate-400">{t.footer.about}</p>
          <span
            className="inline-block mt-4 rounded-full border border-amber2/40 bg-amber2/10 px-3 py-1 text-xs font-mono uppercase tracking-wider text-amber2"
            data-testid="footer-rebrand-badge"
          >
            {t.footer.rebrand}
          </span>
        </div>

        <div>
          <h3 className="font-display font-semibold text-white mb-4">{t.footer.quickLinks}</h3>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/" className="hover:text-white transition-colors duration-200">{t.nav.home}</Link></li>
            <li><Link to="/services" className="hover:text-white transition-colors duration-200">{t.nav.services}</Link></li>
            <li><Link to="/projects" className="hover:text-white transition-colors duration-200">{t.nav.projects}</Link></li>
            <li><Link to="/contact" className="hover:text-white transition-colors duration-200">{t.nav.contact}</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="font-display font-semibold text-white mb-4">{t.footer.servicesTitle}</h3>
          <ul className="space-y-2.5 text-sm">
            {SERVICE_IDS.map((id) => (
              <li key={id}>
                <Link to="/services" className="hover:text-white transition-colors duration-200">
                  {t.servicesPage.items.find((s) => s.id === id)?.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-display font-semibold text-white mb-4">{t.footer.contactTitle}</h3>
          <ul className="space-y-3 text-sm">
            <li>
              <a href={SITE.phoneHref} className="flex items-start gap-2.5 hover:text-white transition-colors duration-200">
                <Phone className="w-4 h-4 mt-0.5 text-electric" />
                {SITE.phoneDisplay}
              </a>
            </li>
            <li>
              <a href={SITE.whatsappHref} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2.5 hover:text-white transition-colors duration-200">
                <MessageCircle className="w-4 h-4 mt-0.5 text-wa" />
                WhatsApp
              </a>
            </li>
            <li>
              <a href={`mailto:${SITE.email}`} className="flex items-start gap-2.5 hover:text-white transition-colors duration-200 break-all">
                <Mail className="w-4 h-4 mt-0.5 text-electric" />
                {SITE.email}
              </a>
            </li>
            <li className="flex items-start gap-2.5">
              <MapPin className="w-4 h-4 mt-0.5 text-electric" />
              {SITE.address}
            </li>
            <li className="flex items-start gap-2.5">
              <Clock className="w-4 h-4 mt-0.5 text-electric" />
              {SITE.hours}
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-400">© {year} {SITE.name}. {t.footer.rights}</p>
          <p className="text-xs text-slate-500 max-w-md text-center sm:text-right">{t.footer.sampleNote}</p>
        </div>
      </div>
    </footer>
  );
}
